import { IQuery, ISourceSchema } from "@types"
import { hasValue, toBool, toJSON, toNum, toStr } from "uxp/components"

export interface IFilterConditionsEditorProps {
    filterConditions: IFilterConditionGroup
    onChange: (newFilterConditions: IFilterConditionGroup) => void
    attributes: ISourceSchema,
    onChnageConfitions?: (conditions?: IFilterConditionGroup) => void,
    query: IQuery
}

export interface IFilterConditionGroupEditorProps {
    group: IFilterConditionGroup,
    onChange: (updated: IFilterConditionGroup) => void
    onRemove: () => void,
    attributes: ISourceSchema
    isParent?: boolean,
    query: IQuery
}

export interface IFilterConditionEditorProps {
    condition: IFilterCondition,
    onChange: (updated: IFilterCondition) => void,
    attributes: ISourceSchema,
    query: IQuery
}

export interface IFilterConditionGroup {
    All: boolean,
    Conditions: (IFilterCondition | IFilterConditionGroup)[]
}


export type FCOperator = "$eq" | "$ne" | "$gt" | "$lt" | "$gte" | "$lte" | "$in" | "$regex" | '$empty' | '$nempty' | string

export function operatorHasRValue(op: FCOperator) {
    return !(
        (op == '$empty') || (op == '$nempty')
    );
}
export const filterConditionOperators: { [key in FCOperator]: { label: string, value: FCOperator } } = {
    "$eq": { label: "is equal to", value: '$eq' },
    "$ne": { label: "is not equal to", value: "$ne" },
    "$gt": { label: "is greater than", value: "$gt" },
    "$lt": { label: "is less than", value: "$lt" },
    "$gte": { label: "is greater than or equal", value: "$gte" },
    "$lte": { label: "is less than or equal", value: "$lte" },
    "$in": { label: "is in", value: '$in' },
    "$regex": { label: "matches expression", value: "$regex" },
    "$empty": { label: "is empty", value: "$empty" },
    "$nempty": { label: "is not empty", value: "$nempty" },
}

export interface IFilterCondition {
    Attribute: string,
    Operator: FCOperator,
    Value: any,
    DataType?: string
}

export const EmptyFilterCondition: IFilterCondition = { Attribute: '', Operator: "$eq", Value: '' }
export const EmptyFilterConditionGroup: IFilterConditionGroup = { All: false, Conditions: [] }


export function getInitialFilterConditions(filterConditions?: IFilterConditionGroup) {
    const filters = Object.assign({}, EmptyFilterConditionGroup)
    if (!!filterConditions) {
        filters.All = filterConditions.All
        filters.Conditions = filterConditions.Conditions || []
    }

    return filters
}

function parseToArray(value: any): any[] {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const _parsed = toJSON(value, null)
        if (!!_parsed) return _parsed
        return value.split(',').map(item => item.trim());
    }
    return [];
}

export function convertToDataType(value: any, dataType: string) {
    switch (dataType) {
        case 'number':
            return toNum(value, 0)
        case 'boolean':
            return toBool(value, false)
        default:
            return toStr(value, '')
    }
}

export function isValidInputValue(value: any, dataType: string) {

    if (dataType == 'number') return /^\d+$/.test(value);
    if (dataType == 'boolean') return /^(true|false|1|0)$/i.test(value);
    return true
}

export function getQueryFromFilterConditions(filters: IFilterConditionGroup) {
    const query: any = {
        [filters.All ? '$and' : '$or']: []
    }

    if (filters.Conditions.length > 0) {
        const conditions: any[] = filters.Conditions.map((c, i) => {
            if (c.hasOwnProperty('All')) {
                // group
                return getQueryFromFilterConditions(c as IFilterConditionGroup)
            }
            else {
                //condition 
                const { Attribute, Operator, Value, DataType } = c as IFilterCondition

                // // TODO handle null, not null 
                // //  handle $in 
                // let parsedValue = getParsedValue(Value, DataType, Operator);
                //  convertToDataType(Value, DataType)
                // if (Operator == '$in') {
                //     if (!(String(parsedValue))?.trim()?.startsWith('{$')) {
                //         parsedValue = parseToArray(parsedValue)
                //     }
                // }

                return { [Attribute]: { [Operator]: Value } }
            }
        })

        query[filters.All ? '$and' : '$or'] = conditions || []
    }

    return query
}


export function getFilterConditionsFromQuery(query: any): IFilterConditionGroup {
    // Handle null, undefined, or empty object cases
    if (!query || Object.keys(query).length === 0) {
        return Object.assign({ ...EmptyFilterConditionGroup } as IFilterConditionGroup);
    }

    // Determine if the query is an OR condition
    const isAll = query.hasOwnProperty("$and");
    const conditionsArray = isAll ? query["$and"] : query["$or"];

    // Initialize conditions
    const conditions: (IFilterCondition | IFilterConditionGroup)[] = [];

    // Iterate over each condition in the array
    conditionsArray?.forEach((item: any) => {
        if (item.hasOwnProperty("$or") || item.hasOwnProperty("$and")) {
            // Recursively convert nested groups
            conditions.push(getFilterConditionsFromQuery(item));
        } else {
            // Convert individual conditions
            if (Object.keys(item).length == 0) conditions.push(Object.assign({}))
            else {
                Object.keys(item).forEach(attribute => {

                    const attrValue = item[attribute]
                    let operator = "$eq";
                    let value = ''

                    if (typeof attrValue == 'object') {
                        if (Object.keys(attrValue).length > 0) {
                            operator = Object.keys(attrValue)?.[0] || null
                            value = (!!operator) ? attrValue[operator] : ''
                        }
                    }
                    else {
                        value = attrValue
                    }

                    conditions.push({
                        Attribute: attribute,
                        Operator: operator || '$eq',
                        Value: value || '',
                        DataType: !!value ? typeof value : 'string'
                    })

                });
            }
        }
    });

    return {
        All: isAll,
        Conditions: conditions
    };
}

export function validateFilterConditions(query: any): { isValid: boolean, error?: string, uniqueAttributes?: string[] } {
    if (!query || typeof query !== 'object' || Array.isArray(query) || Object.keys(query).length == 0) {
        return { isValid: false, error: 'Atleast one condition is required' }
    }

    const filterConditions = getFilterConditionsFromQuery(query);
    const uniqueAttributes = new Set<string>();


    function isConditionConfigured(condition: IFilterCondition): boolean {
        if (hasValue(condition?.Attribute) && hasValue(condition?.Operator)) {
            // For $empty and $nempty operators, value is not required
            if (['$empty', '$nempty'].includes(condition.Operator)) {
                uniqueAttributes.add(condition.Attribute);
                return true;
            }
            // For other operators, value is required
            if (!!condition?.Value) {
                uniqueAttributes.add(condition.Attribute);
                return true;
            }
        }
        return false;
    }

    function isGroupConfigured(group: IFilterConditionGroup): boolean {
        return group.Conditions.every(c => {
            if (c.hasOwnProperty('All')) return (c as IFilterConditionGroup).Conditions?.length > 0 && isGroupConfigured(c as IFilterConditionGroup)
            else return isConditionConfigured(c as IFilterCondition)
        });
    }

    const isConfigured = isGroupConfigured(filterConditions);

    if (!isConfigured) {
        return { isValid: false, error: "All conditions needs be configured properly" };
    }

    const uniqueAttributesArray = Array.from(uniqueAttributes);
    return { isValid: true, uniqueAttributes: uniqueAttributesArray };
}